import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { Loader2, CheckCircle2, Circle, Clock } from "lucide-react";
import { getMyProject } from "@/lib/portal/portal.functions";

export const Route = createFileRoute("/_authenticated/portal/project")({
  component: ProjectTimelinePage,
});

const STAGE_LABELS: Record<string, string> = {
  inquiry: "Inquiry",
  estimate_scheduled: "Estimate Scheduled",
  estimate_completed: "Estimate Completed",
  proposal_sent: "Proposal Sent",
  proposal_approved: "Proposal Approved",
  deposit_received: "Deposit Received",
  materials_ordered: "Materials Ordered",
  project_scheduled: "Project Scheduled",
  preparation: "Preparation",
  painting: "Painting",
  cleanup: "Cleanup",
  final_walkthrough: "Final Walkthrough",
  completed: "Completed",
  warranty_active: "Warranty Active",
};

function ProjectTimelinePage() {
  const fn = useServerFn(getMyProject);
  const { data, isLoading } = useQuery({ queryKey: ["portal", "project"], queryFn: () => fn() });

  if (isLoading) return <div className="py-20 grid place-items-center"><Loader2 className="size-6 animate-spin text-amber-300" /></div>;
  if (!data?.project) return <div className="py-20 text-center text-zinc-500">No project yet.</div>;

  const timeline = data.timeline as Array<any>;

  return (
    <div className="space-y-8">
      <header>
        <p className="text-amber-300 text-[11px] tracking-[0.3em] uppercase font-semibold">Project</p>
        <h1 className="font-serif text-4xl text-white mt-1">Timeline</h1>
        <p className="text-zinc-500 text-sm mt-1">Every step of your project — from inquiry through warranty activation.</p>
      </header>

      <ol className="relative border-l border-white/10 ml-3 space-y-6">
        {timeline.map((t) => {
          const done = t.status === "complete";
          const active = t.status === "in_progress";
          return (
            <li key={t.id} className="pl-8 relative">
              <span className={`absolute -left-[13px] top-1.5 size-6 rounded-full grid place-items-center border ${done ? "bg-amber-300 border-amber-300 text-zinc-950" : active ? "bg-amber-300/20 border-amber-300/50 text-amber-200" : "bg-[#0a0a0a] border-white/10 text-zinc-400"}`}>
                {done ? <CheckCircle2 className="size-3.5" /> : active ? <Clock className="size-3.5 animate-pulse" /> : <Circle className="size-2.5" />}
              </span>
              <div className={`rounded-2xl border p-5 ${done ? "border-amber-300/20 bg-amber-300/[0.03]" : active ? "border-amber-300/30 bg-amber-300/[0.05]" : "border-white/10 bg-white/[0.02]"}`}>
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <h3 className="font-serif text-lg text-white">{STAGE_LABELS[t.stage] ?? t.stage}</h3>
                  <span className={`text-[10px] uppercase tracking-wider font-semibold px-2.5 py-1 rounded-full ${done ? "bg-amber-300/15 text-amber-200" : active ? "bg-amber-300/20 text-amber-100" : "bg-white/5 text-zinc-500"}`}>
                    {t.status.replace("_", " ")}
                  </span>
                </div>
                {t.occurred_on && <p className="text-xs text-zinc-500 mt-1">{new Date(t.occurred_on).toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric", year: "numeric" })}</p>}
                {t.est_completion && !done && <p className="text-xs text-zinc-500 mt-1">Estimated: {new Date(t.est_completion).toLocaleDateString()}</p>}
                {t.notes && <p className="text-sm text-zinc-400 mt-3 leading-relaxed">{t.notes}</p>}
              </div>
            </li>
          );
        })}
      </ol>
    </div>
  );
}